/**
 * Health tab — operational readiness at a glance, from `useHealth()` plus a
 * freshness read off `useRuns(10)`:
 *
 *   - version / org / env header (HealthResponse.version|org_id|env).
 *   - secret slots grid: one cell per registry secret (HealthResponse.secrets),
 *     a set/unset dot + the lastModified age. Mirrors the presence dots the
 *     Settings tab derives from the same registry sweep.
 *   - per-routine freshness: the latest `morning` and latest `eod` run from the
 *     recent index rows, each with its age and a >36h stale warning (a routine
 *     fires ~daily, so >36h means a missed day).
 *   - endpoint params: the API base (config.apiUrl) + the site URL
 *     (window.location.origin) so the operator can confirm what the SPA is
 *     pointed at.
 *
 * Tolerant throughout: a row with no parseable timestamp reads as "unknown age"
 * rather than throwing; a routine with no recent run reads as "no run".
 *
 * Styling is deferred to Task 8; only semantic classNames are emitted here.
 */

import { useAuth } from '../lib/auth';
import { useHealth, useRuns, type HealthSecret, type RunIndexRow } from '../lib/queries';

// ---------------------------------------------------------------------------
// Pure helpers (exported for unit tests — no DOM needed).
// ---------------------------------------------------------------------------

/** A routine is considered stale when its latest run is older than this. A
 *  morning/eod routine fires ~daily, so >36h means at least one missed day
 *  (covers a normal overnight gap + weekend slack without false alarms). */
export const STALE_THRESHOLD_HOURS = 36;

/** Whole hours between `then` and `now` (>=0); null when `then` is unparseable. */
export function ageHours(then: string | null | undefined, now: Date): number | null {
  if (!then) return null;
  const t = Date.parse(then);
  if (Number.isNaN(t)) return null;
  const ms = now.getTime() - t;
  return ms < 0 ? 0 : Math.floor(ms / 3_600_000);
}

/** Compact human age: "3h", "2d 4h", or "—" for an unknown timestamp. */
export function fmtAge(then: string | null | undefined, now: Date): string {
  const h = ageHours(then, now);
  if (h === null) return '—';
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  const rem = h % 24;
  return rem ? `${d}d ${rem}h` : `${d}d`;
}

export interface RoutineFreshness {
  routine: string;
  /** The latest matching run, or null when none is present. */
  run: RunIndexRow | null;
  /** The timestamp used for the age (completed_at, else started_at). */
  at: string | null;
  ageHours: number | null;
  stale: boolean;
}

/** Latest run per routine, with a >36h stale flag. The index is server-sorted
 *  newest-first (scanForward:false), so the FIRST row matching a routine is the
 *  latest; we don't re-sort here (a missing/odd timestamp must not reorder). */
export function routineFreshness(
  runs: RunIndexRow[] | undefined,
  routine: string,
  now: Date,
): RoutineFreshness {
  const run = (runs ?? []).find((r) => r.routine === routine) ?? null;
  const at = run ? ((run.completed_at as string | undefined) ?? (run.started_at as string | undefined) ?? null) : null;
  const age = ageHours(at, now);
  // A routine with no run at all is "stale" (nothing has ever succeeded
  // recently); a run whose age is unknown is NOT flagged (we can't claim it's
  // late). A known age over the threshold is stale.
  const stale = run === null ? true : age !== null && age > STALE_THRESHOLD_HOURS;
  return { routine, run, at, ageHours: age, stale };
}

// ---------------------------------------------------------------------------
// Secret slot.
// ---------------------------------------------------------------------------

function SecretSlot({ secret, now }: { secret: HealthSecret; now: Date }) {
  return (
    <li className="secret-slot" data-key={secret.key} data-set={secret.set ? 'true' : 'false'}>
      <span
        className={`secret-dot ${secret.set ? 'secret-dot-set' : 'secret-dot-unset'}`}
        aria-label={secret.set ? 'set' : 'unset'}
        title={secret.set ? 'set' : 'unset'}
      />
      <span className="secret-title">{secret.title}</span>
      <span className="secret-key">{secret.key}</span>
      <span className="secret-age">{secret.set ? fmtAge(secret.lastModified, now) : 'unset'}</span>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Routine freshness card.
// ---------------------------------------------------------------------------

function FreshnessCard({ fresh, now }: { fresh: RoutineFreshness; now: Date }) {
  return (
    <li
      className={`freshness-card ${fresh.stale ? 'freshness-stale' : 'freshness-ok'}`}
      data-routine={fresh.routine}
      data-stale={fresh.stale ? 'true' : 'false'}
    >
      <span className="freshness-routine">{fresh.routine}</span>
      {fresh.run ? (
        <>
          <span className="freshness-run-id">{fresh.run.run_id}</span>
          <span className="freshness-age">{fmtAge(fresh.at, now)} ago</span>
          {fresh.stale && (
            <span className="freshness-warning" role="alert">
              stale — no run in over {STALE_THRESHOLD_HOURS}h
            </span>
          )}
        </>
      ) : (
        <span className="freshness-warning" role="alert">no recent run</span>
      )}
    </li>
  );
}

// ---------------------------------------------------------------------------
// The tab.
// ---------------------------------------------------------------------------

export default function HealthTab() {
  const { config } = useAuth();
  const health = useHealth();
  const runs = useRuns(10);

  if (health.isLoading) return <p className="health-loading">Loading health…</p>;
  if (health.isError) {
    return (
      <p className="health-error error" role="alert">
        Failed to load health: {health.error instanceof Error ? health.error.message : 'unknown error'}
      </p>
    );
  }

  const data = health.data!;
  const now = new Date();
  const secrets = data.secrets ?? [];
  const morning = routineFreshness(runs.data?.runs, 'morning', now);
  const eod = routineFreshness(runs.data?.runs, 'eod', now);

  // window.location is the SPA's own URL; guard for non-DOM contexts.
  const siteUrl = typeof window !== 'undefined' && window.location ? window.location.origin : '—';

  return (
    <section className="health-tab" aria-label="Health">
      <header className="health-header">
        <span className="health-version" data-version={data.version}>version {data.version}</span>
        <span className="health-org">org {data.org_id}</span>
        <span className="health-env">env {data.env}</span>
        <span className={`health-ok ${data.ok ? 'health-ok-true' : 'health-ok-false'}`}>
          {data.ok ? 'ok' : 'degraded'}
        </span>
      </header>

      <section className="health-secrets" aria-label="Secret slots">
        <h3 className="section-title">Secrets</h3>
        {secrets.length === 0 ? (
          <p className="health-secrets-empty">No secret slots registered.</p>
        ) : (
          <ul className="secret-grid">
            {secrets.map((s) => (
              <SecretSlot key={s.key} secret={s} now={now} />
            ))}
          </ul>
        )}
      </section>

      <section className="health-freshness" aria-label="Routine freshness">
        <h3 className="section-title">Routine freshness</h3>
        {runs.isError ? (
          <p className="health-freshness-error error" role="alert">
            Failed to load recent runs for freshness.
          </p>
        ) : (
          <ul className="freshness-grid">
            <FreshnessCard fresh={morning} now={now} />
            <FreshnessCard fresh={eod} now={now} />
          </ul>
        )}
      </section>

      <section className="health-endpoints" aria-label="Endpoints">
        <h3 className="section-title">Endpoints</h3>
        <dl className="endpoint-list">
          <div className="endpoint-row">
            <dt>API</dt>
            <dd className="endpoint-api">{config.apiUrl}</dd>
          </div>
          <div className="endpoint-row">
            <dt>Site</dt>
            <dd className="endpoint-site">{siteUrl}</dd>
          </div>
        </dl>
      </section>
    </section>
  );
}
